import type { TFunction } from 'i18next';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { OverviewReply } from '../api-types';
import { formatTime } from '../format';
import { formatLeft, nextRefreshOf, useCountdown } from './use-countdown';

function countdownText(left: number | undefined, t: TFunction<'archive'>): string | null {
  if (left === undefined) return null;
  if (left <= 0) return t('overview.updating');
  return t('overview.nextRefresh', { left: formatLeft(left) });
}

/**
 * «Ultimo controllo» with the live countdown to the next automatic refresh (#798): at zero it says
 * «Aggiornamento in corso…» and reloads the data (again every 15 s while the new run is late).
 */
export function RefreshLine({
  reply,
  onDue,
}: {
  readonly reply: OverviewReply;
  readonly onDue: () => void;
}) {
  const { t } = useTranslation('archive');
  const left = useCountdown(nextRefreshOf(reply), onDue);
  const proposal = reply.rulesStatus === 'proposta';
  // Both are null before the first scan; `rulesVersion` is null when no rules are loaded.
  const lastCheck = reply.refresh.lastRefreshAt ?? reply.scannedAt;
  return (
    <div className="flex flex-col gap-1 text-sm sm:items-end">
      <p className="flex items-center gap-1.5">
        <Clock aria-hidden="true" className="size-4" />
        <span className="text-muted-foreground">{t('overview.lastCheck')}</span>
        <strong>{lastCheck === null ? '—' : formatTime(lastCheck)}</strong>
      </p>
      <p role="status" aria-live="polite" className="text-muted-foreground">
        {countdownText(left, t)}
      </p>
      {reply.rulesVersion === null ? null : (
        <Link
          to="/archive/rules"
          className="inline-flex min-h-11 items-center font-semibold underline"
        >
          {t(proposal ? 'overview.rulesProposal' : 'overview.rulesVersion', {
            version: reply.rulesVersion,
          })}
        </Link>
      )}
    </div>
  );
}
